import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import {
  getMergedSelectedSectionSettings,
  useListingScoreRecommendations,
} from 'src/components/Listings/InventoryEventPage/InventoryEventDetail/InventoryEventDetail.hooks';
import { ListingPricingInputWithId } from 'src/components/Listings/MapWithCompListingsPreview/MapWithCompListingsPreview.hooks';
import { useListingGroupPricingSettings } from 'src/hooks/api/useListingGroupPricingSettings';
import { useGetListingAutoPricingSettings } from 'src/hooks/useGetListingAutoPricingSettings';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { isSuccess } from 'src/utils/errorUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Event,
  Feature,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingDetailsAutoPricingSectionUpdates,
  ListingDetailsPricingSectionUpdates,
  ListingGroup,
  ListingGroupAutoPriceSettings,
  PricingClient,
  SelectedSectionSettings,
  UpdateListingGroupAutoPriceSettingsFromOnePagerRequest,
} from 'src/WebApiController';

import { useActivePosEntityContext } from '../ActivePosEntityContext';
import { useAppContext } from '../AppContext';
import { useCatalogDataContext } from '../CatalogDataContext';
import { useClientLogContext } from '../ClientLogContext';
import { useEventMapContext } from '../EventMapContext';

export const useMobileListingDrawerPricingSettings = (
  listing?: Listing,
  listingGroup?: ListingGroup,
  event?: Event
) => {
  const hasSelectedSectionScoreRecommendationsFeature = useUserHasFeature(
    Feature.SelectedSectionScoreRecommendations
  );
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { activeAccountWebClientConfig } = useAppContext();
  const { venueMapInfo } = useEventMapContext();
  const { updateActivePosEntityInfo } = useActivePosEntityContext();
  const { showErrorDialog } = useClientLogContext();
  const queryClient = useQueryClient();

  const { updateItemInEvent } = useCatalogDataContext();

  const client = useMemo(() => {
    if (activeAccountWebClientConfig?.activeAccountId) {
      return new ListingClient(activeAccountWebClientConfig);
    }
    return null;
  }, [activeAccountWebClientConfig]);

  const {
    pricingSettings: listingPricingSettings,
    loaded: listingSettingLoaded,
  } = useGetListingAutoPricingSettings(listing, event);

  const {
    pricingSettings: listingGroupPricingSettings,
    isLoading: isListingGroupPricingSettingsLoading,
  } = useListingGroupPricingSettings(listingGroup?.ltGrpId, event);

  const { sectionRowScoreRecommendations } = useListingScoreRecommendations(
    event,
    venueMapInfo,
    listing,
    !hasSelectedSectionScoreRecommendationsFeature
  );

  // Recommendation only applies to settings with no section filters
  const sanitizedRecommendations = useMemo(() => {
    if (
      listingPricingSettings?.compListingSelectedSectionSettings
        ?.sectionIdFilter &&
      listingPricingSettings.compListingSelectedSectionSettings.sectionIdFilter
        .length > 0
    ) {
      return undefined;
    }
    return sectionRowScoreRecommendations;
  }, [
    listingPricingSettings?.compListingSelectedSectionSettings?.sectionIdFilter,
    sectionRowScoreRecommendations,
  ]);

  const pricingSettings = useMemo<
    ListingDetailsAutoPricingSectionUpdates | undefined
  >(() => {
    if (listingSettingLoaded && listing?.id && listingPricingSettings) {
      if (listing?.id !== listingPricingSettings.id) {
        // Only reset the form when listing pricing settings is for the exact selected listing
        // This is to avoid the race condition where switching selectedListing triggered different
        // GetListingAutoPricingSettings that can result in the wrong listing settings being assigned
        return;
      }
      if (hasSelectedSectionScoreRecommendationsFeature) {
        const selectedSectionSettingsWithRecommendations =
          getMergedSelectedSectionSettings(
            listingPricingSettings?.compListingSelectedSectionSettings,
            sanitizedRecommendations?.recommendedSelectedSectionSettings
          );
        const pricingSettingsWithScoreRecommendations = {
          ...listingPricingSettings,
          compListingSelectedSectionSettings:
            selectedSectionSettingsWithRecommendations,
        } as ListingDetailsAutoPricingSectionUpdates;
        return pricingSettingsWithScoreRecommendations ?? {};
      } else {
        return listingPricingSettings ?? {};
      }
    }
  }, [
    hasSelectedSectionScoreRecommendationsFeature,
    listing?.id,
    listingPricingSettings,
    listingSettingLoaded,
    sanitizedRecommendations?.recommendedSelectedSectionSettings,
  ]);

  const sanitizedPricingSettingsInput = useMemo(():
    | ListingPricingInputWithId
    | undefined => {
    if (!pricingSettings) {
      return undefined;
    }
    const {
      id,
      netProceedsFloor,
      netProceedsCeiling,
      autoPricingEnabled,
      currencyCode,

      compListingFloor,
      compListingCeiling,
      compListingMode,
      compListingOnlyForSameZoneEnabled,
      compListingOnlyForSelectedSectionsEnabled,
      compListingExcludeFanInventory,
      compListingExcludeDefects,
      compListingIncludeUnmappedInventory,
      compListingQuantityScoreAdjustmentEnabled,
      compListingQuantityScoreAdjustmentOverrideJson,
      compListingSelectedSectionSettings,
      compListingQuantityFilters,

      undercutMode,
      undercutAbsoluteAmount,
      undercutRelativeAmount,

      circuitBreakerMaxDiscountVelocityPercent,
      circuitBreakerMaxDiscountVelocityTicksInHours,
      circuitBreakerMinCompListingCount,
      circuitBreakerRelativeCeiling,
      circuitBreakerRelativeFloor,

      outlierMode,
      outlierStandardDeviations,
      outlierKthLowestLimit,
      outlierKthLowestLimitRelativeSpacing,
      outlierKthLowestLimitAbsoluteSpacing,
    } = pricingSettings as ListingDetailsAutoPricingSectionUpdates;

    const mergedInput = {
      id,
      netProceedsFloor,
      netProceedsCeiling,
      autoPricingEnabled,
      currencyCode,

      compListingFloor,
      compListingCeiling,
      compListingMode,
      compListingOnlyForSameZoneEnabled,
      compListingOnlyForSelectedSectionsEnabled,
      compListingExcludeFanInventory,
      compListingExcludeDefects,
      compListingIncludeUnmappedInventory,
      compListingQuantityScoreAdjustmentEnabled,
      compListingQuantityScoreAdjustmentOverrideJson,
      compListingSelectedSectionSettings,
      compListingQuantityFilters,

      undercutMode,
      undercutAbsoluteAmount,
      undercutRelativeAmount,

      circuitBreakerMaxDiscountVelocityPercent,
      circuitBreakerMaxDiscountVelocityTicksInHours,
      circuitBreakerMinCompListingCount,
      circuitBreakerRelativeCeiling,
      circuitBreakerRelativeFloor,

      outlierMode,
      outlierStandardDeviations,
      outlierKthLowestLimit,
      outlierKthLowestLimitRelativeSpacing,
      outlierKthLowestLimitAbsoluteSpacing,
    } as ListingPricingInputWithId;

    return mergedInput;
  }, [pricingSettings]);

  const sanitizedListingGroupPricingSettings = useMemo(() => {
    if (
      isListingGroupPricingSettingsLoading ||
      !listingGroup ||
      listingGroup.ltGrpId !== listingGroupPricingSettings?.listingGroupId
    ) {
      return undefined;
    }

    return {
      ...listingGroupPricingSettings,
      deprioritizedQuantities: listingGroup.deprioritizedQuantities,
      desiredActiveListings: listingGroup.desiredActiveListings,
      undAbsAmt: listingGroup.absoluteRankPremium,
      undRelAmt: listingGroup.relativeRankPremium,
      autoPricingEnabled: listingGroup.isAutoPrc,
      netProceedsFloor: listingGroup.procsFloor,
      netProceedsCeiling: listingGroup.procsCeil,
      startingUnitNetProceeds: null,
    } as ListingGroupAutoPriceSettings;
  }, [
    isListingGroupPricingSettingsLoading,
    listingGroup,
    listingGroupPricingSettings,
  ]);

  const onListingUpdated = useCallback(
    async (dataToInclude?: ListingDetailDataField[]) => {
      if (!listing || !client) {
        return;
      }
      const updatedListing = await client.getListingByListingId(
        listing.id,
        undefined,
        dataToInclude
      );

      if (updatedListing) {
        updateActivePosEntityInfo({
          event,
          posEntity: updatedListing,
          posEntityId: updatedListing.id,
          posEntityDisplayId: updatedListing.idOnMkp,
        });
        updateItemInEvent(updatedListing, ActionOutboxEntityType.Listing);
      }
    },
    [client, event, listing, updateActivePosEntityInfo, updateItemInEvent]
  );

  const onSavePricingSettings = useCallback(
    (selectedSectionSettings: SelectedSectionSettings | null) => {
      if (!hasAutoPricingFeature || !listing || !client) {
        return;
      }
      const input = {
        ...listingPricingSettings,
        compListingSelectedSectionSettings: selectedSectionSettings,
      } as ListingDetailsAutoPricingSectionUpdates;
      tryInvokeApi(
        async () => {
          setIsLoading(true);
          const result =
            await client.updateListingAutoPricingSectionByListingId(
              listing.id,
              input
            );

          if (result) {
            await onListingUpdated([
              ListingDetailDataField.Pricing,
              ListingDetailDataField.AutoPricing,
              ListingDetailDataField.Basic,
            ]);
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListingAutoPricingSection',
            error,
            {
              trackErrorData: {
                listingId: listing.id,
                input: JSON.stringify(input),
              },
            }
          );
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      client,
      hasAutoPricingFeature,
      listing,
      listingPricingSettings,
      onListingUpdated,
      showErrorDialog,
    ]
  );

  const onSaveListingGroupPricingSettings = useCallback(
    (selectedSectionSettings: SelectedSectionSettings | null) => {
      if (
        !hasAutoPricingFeature ||
        !listingGroup ||
        listingGroup.ltGrpId !== listingGroupPricingSettings?.listingGroupId
      ) {
        return;
      }

      const input = {
        ...sanitizedListingGroupPricingSettings,
        compListingSelectedSectionSettings: selectedSectionSettings,
      } as ListingGroupAutoPriceSettings;
      tryInvokeApi(
        async () => {
          setIsLoading(true);
          const result = await new PricingClient(
            activeAccountWebClientConfig
          ).updateListingGroupPricingSettingsFromOnePager(
            listingGroup?.ltGrpId ?? undefined,
            event?.viagId ?? undefined,
            {
              autoPricingSettings: input,
              hasChangesInCompSettings: true,
              hasChangesInUndercutSettings: false,
              hasChangesInOutlierSettings: false,
            } as UpdateListingGroupAutoPriceSettingsFromOnePagerRequest
          );
          if (isSuccess(result)) {
            await queryClient.invalidateQueries({
              queryKey: [
                'PricingClient.getListingGroupAutoPricingSettings',
                listingGroup?.ltGrpId,
                listingGroup?.viagEvId,
              ],
            });
          }
        },
        (error) => {
          showErrorDialog(
            'ListingClient.updateListingAutoPricingSection',
            error,
            {
              trackErrorData: {
                listingGroupId: listingGroup.ltGrpId,
                input: JSON.stringify(input),
              },
            }
          );
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      event?.viagId,
      hasAutoPricingFeature,
      listingGroup,
      listingGroupPricingSettings?.listingGroupId,
      queryClient,
      sanitizedListingGroupPricingSettings,
      showErrorDialog,
    ]
  );

  // All in price is inferred from seller fees, use list price for updates
  const onSaveListingPrice = useCallback(
    (listPrice: number, editingListing?: Listing, callback?: () => void) => {
      const inputListing = editingListing ?? listing;
      if (!inputListing || !client) {
        return;
      }
      tryInvokeApi(
        async () => {
          setIsLoading(true);
          const input: ListingDetailsPricingSectionUpdates = {
            id: inputListing.id,
            rowVersion: null,
            listPrice,
            allInPrice: null, // will be inferred from list price
            buyerUserId: null,
            marketplacePriceUpdates: [],
            autoPricingEnabled: false, // updating manual price will disable auto price
            netProceedsFloor: inputListing.procsFloor,
            pricerSellerUserId: inputListing.pricerId,
            netProceedsCeiling: inputListing.procsCeil,
            currencyCode: inputListing.currency,
          };
          const result = await client.updateListingPricingSectionByListingId(
            input.id,
            input
          );
          if (isSuccess(result)) {
            await onListingUpdated([
              ListingDetailDataField.Basic,
              ListingDetailDataField.Pricing,
            ]);
            callback?.();
          }
        },
        (error) => {
          showErrorDialog('ListingClient.updateListingPricingSection', error, {
            trackErrorData: { listingId: inputListing.id, listPrice },
          });
        },
        () => {
          setIsLoading(false);
        }
      );
    },
    [client, listing, onListingUpdated, showErrorDialog, setIsLoading]
  );

  return {
    isLoading,
    pricingSettings,
    listingGroupPricingSettings: sanitizedListingGroupPricingSettings,
    pricingSettingsInput: sanitizedPricingSettingsInput,
    sectionRowRecommendations: sanitizedRecommendations,
    onSaveListingPrice,
    onSavePricingSettings,
    onSaveListingGroupPricingSettings,
  };
};
